import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
import { toast } from 'sonner';
import { SiteContactsTab } from '@/pages/insurer/SiteContactsTab';
import { SiteIntakeReadonly } from '@/components/SiteIntakeReadonly';
import { useAuth, hasPermission } from '@/lib/auth';
import {
  ArrowLeft, ExternalLink, MapPin, Building2, ChevronRight, Plus, FileText, ShieldCheck,
  IdCard, Users2, Search, RefreshCw, Trash2, Save, Send, ClipboardList,
} from 'lucide-react';

interface ClientCover {
  id: string;
  cover_number: string;
  status: 'draft' | 'issued';
  insured_name?: string;
  site_id?: string;
  policy_period_start?: string;
  policy_period_end?: string;
  totals?: { total_sum_insured_zar?: number; total_premium_zar?: number };
}

interface ClientDetail {
  id: string;
  name: string;
  trading_name?: string;
  industry?: string;
  registration_number?: string;
  vat_number?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  physical_address?: string;
  notes?: string;
  broker_id?: string;
  broker_name?: string | null;
  broker_fsp?: string | null;
  site_count?: number;
  active?: boolean;
  created_at?: string;
}

interface SiteRow {
  id: string;
  name: string;
  site_reference?: string;
  city?: string;
  province?: string;
  physical_address?: string;
  site_contact_name?: string;
  site_contact_phone?: string;
}

interface ClientUser {
  id: string;
  client_id: string;
  full_name: string;
  role: string;
  phone: string;
  email?: string;
  approved_by?: string;
  date_approved: string;
  registered: boolean;
  access_status: 'Active' | 'Revoked';
}

const fmtZAR = (n?: number | null): string =>
  (n == null || Number.isNaN(n)) ? '—' : `R\u00a0${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(iso).slice(0, 10); }
};

export const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams();
  const nav = useNavigate();
  const { role } = useAuth();
  const canDelete = hasPermission(role, 'clients', 'delete');
  const delClient = async (): Promise<void> => {
    if (!clientId) return;
    if (!window.confirm('Delete this client and all of its sites? This cannot be undone.')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted.');
      nav('/insurer/clients');
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not delete client.');
    }
  };
  const [searchParams] = useSearchParams();
  const initialTab = ['detail', 'sites', 'users', 'site-contacts'].includes(searchParams.get('tab') || '')
    ? (searchParams.get('tab') as string)
    : 'detail';
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [covers, setCovers] = useState<ClientCover[]>([]);
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [brokerOptions, setBrokerOptions] = useState<Array<{ id: string; brokerage_name: string; fsp_number: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('');
  const [userForm, setUserForm] = useState({ full_name: '', phone: '', email: '', role: 'Client Admin' });

  // Inline edit mode for the "Client Detail" tab \u2014 mirrors the broker pattern.
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [edit, setEdit] = useState<{
    name: string; trading_name: string; industry: string;
    registration_number: string; vat_number: string;
    primary_contact_name: string; primary_contact_phone: string; primary_contact_email: string;
    physical_address: string; notes: string;
    broker_id: string;
  }>({
    name: '', trading_name: '', industry: '',
    registration_number: '', vat_number: '',
    primary_contact_name: '', primary_contact_phone: '', primary_contact_email: '',
    physical_address: '', notes: '',
    broker_id: '',
  });

  const load = async (): Promise<void> => {
    if (!clientId) return;
    try {
      const [c, s, cv, u] = await Promise.all([
        api.get<ClientDetail>(`/clients/${clientId}`),
        api.get<SiteRow[]>(`/sites`, { params: { client_id: clientId } }),
        api.get<ClientCover[]>(`/clients/${clientId}/covers`),
        api.get<ClientUser[]>(`/clients/${clientId}/users`).catch(() => ({ data: [] as ClientUser[] })),
      ]);
      setClient(c.data);
      setSites(s.data);
      setCovers(cv.data);
      setUsers(u.data);
      setEdit({
        name: c.data.name || '',
        trading_name: c.data.trading_name || '',
        industry: c.data.industry || '',
        registration_number: c.data.registration_number || '',
        vat_number: c.data.vat_number || '',
        primary_contact_name: c.data.primary_contact_name || '',
        primary_contact_phone: c.data.primary_contact_phone || '',
        primary_contact_email: c.data.primary_contact_email || '',
        physical_address: c.data.physical_address || '',
        notes: c.data.notes || '',
        broker_id: c.data.broker_id || '',
      });
      // Populate broker dropdown once (non-blocking if it fails)
      if (brokerOptions.length === 0) {
        try {
          const br = await api.get<Array<{ id: string; brokerage_name: string; fsp_number: string }>>('/brokers');
          setBrokerOptions(br.data);
        } catch { /* dropdown just stays empty */ }
      }
    } catch {
      toast.error('Failed to load client.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const filteredSites = useMemo(() => {
    const q = siteFilter.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) => (
      s.name.toLowerCase().includes(q)
      || (s.site_reference || '').toLowerCase().includes(q)
      || (s.city || '').toLowerCase().includes(q)
      || (s.province || '').toLowerCase().includes(q)
      || (s.physical_address || '').toLowerCase().includes(q)
      || (s.site_contact_name || '').toLowerCase().includes(q)
    ));
  }, [sites, siteFilter]);

  const addUser = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!clientId) return;
    if (!userForm.full_name.trim() || !userForm.phone.trim()) {
      toast.error('Name and mobile are required');
      return;
    }
    try {
      await api.post(`/clients/${clientId}/users`, userForm);
      toast.success('User added');
      setUserForm({ full_name: '', phone: '', email: '', role: 'Client Admin' });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Add failed';
      toast.error(msg);
    }
  };
  const revokeUser = async (uid: string): Promise<void> => {
    if (!clientId) return;
    await api.patch(`/clients/${clientId}/users/${uid}/revoke`);
    toast.success('Access revoked'); await load();
  };
  const restoreUser = async (uid: string): Promise<void> => {
    if (!clientId) return;
    await api.patch(`/clients/${clientId}/users/${uid}/restore`);
    toast.success('Access restored'); await load();
  };
  const deleteUser = async (uid: string): Promise<void> => {
    if (!clientId) return;
    if (!confirm('Delete this user record?')) return;
    await api.delete(`/clients/${clientId}/users/${uid}`);
    toast.success('Deleted'); await load();
  };
  const [resendingUser, setResendingUser] = useState<string | null>(null);
  const resendUserWelcome = async (uid: string): Promise<void> => {
    if (!clientId) return;
    setResendingUser(uid);
    try {
      await api.post(`/clients/${clientId}/users/${uid}/resend-welcome`);
      toast.success('Welcome / portal-access message re-sent (email & SMS)');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Resend failed');
    } finally { setResendingUser(null); }
  };

  const saveClientEdit = async (): Promise<void> => {
    if (!clientId) return;
    if (!edit.name.trim()) { toast.error('Client name is required'); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/clients/${clientId}`, {
        name: edit.name.trim(),
        trading_name: edit.trading_name || null,
        industry: edit.industry || null,
        registration_number: edit.registration_number || null,
        vat_number: edit.vat_number || null,
        primary_contact_name: edit.primary_contact_name || null,
        primary_contact_phone: edit.primary_contact_phone || null,
        primary_contact_email: edit.primary_contact_email || null,
        physical_address: edit.physical_address || null,
        notes: edit.notes || null,
        broker_id: edit.broker_id || null,
      });
      toast.success('Client updated');
      setEditing(false);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.detail
        ?? (err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.error
        ?? 'Update failed';
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="text-raptor-navy/60 py-10 text-center" data-testid="client-loading">Loading…</div>;
  if (!client) return <div className="text-raptor-navy/60 py-10 text-center">Client not found.</div>;

  const primaryContactPhone = client.primary_contact_phone || null;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="client-detail-page">
      {/* Breadcrumb + Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1 text-xs font-body text-raptor-navy/60">
            <button
              type="button"
              onClick={() => nav('/insurer/clients')}
              className="hover:text-raptor-blue inline-flex items-center gap-1"
              data-testid="back-to-clients"
            >
              <ArrowLeft size={12} /> Clients
            </button>
            {client.broker_id && (
              <>
                <ChevronRight size={12} className="text-raptor-navy/30" />
                <button
                  type="button"
                  onClick={() => nav(`/insurer/brokers/${client.broker_id}`)}
                  className="hover:text-raptor-blue inline-flex items-center gap-1"
                >
                  <Building2 size={12} /> {client.broker_name || client.broker_id}
                </button>
              </>
            )}
          </div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide mt-1">{client.name}</h2>
          <div className="text-sm text-raptor-navy/60 font-body flex flex-wrap items-center gap-2">
            <span className="font-tabular">{client.id}</span>
            {client.trading_name && (<><span>·</span><span>t/a {client.trading_name}</span></>)}
            {client.industry && (<><span>·</span><span>{client.industry}</span></>)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {client.active
            ? <Badge variant="blue">Active</Badge>
            : <Badge variant="destructive">Inactive</Badge>}
          {canDelete && (
            <Button size="sm" variant="outline" onClick={delClient} data-testid="delete-client-btn"
              className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 size={14} className="mr-1" /> Delete client
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="detail" data-testid="tab-client-detail"><IdCard size={12} /> Client Detail</TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-client-sites"><MapPin size={12} /> All Sites ({sites.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-client-users"><Users2 size={12} /> Users ({users.length})</TabsTrigger>
          <TabsTrigger value="site-contacts" data-testid="tab-client-site-contacts"><Users2 size={12} /> Site Contacts</TabsTrigger>
          <TabsTrigger value="intake" data-testid="tab-client-intake"><ClipboardList size={12} /> Site Intake</TabsTrigger>
        </TabsList>

        {/* ---------- Tab 1: Client Detail ---------- */}
        <TabsContent value="detail" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Client information</CardTitle>
                <CardDescription>Core registration details for {client.name}.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {!editing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-client-inline-btn">Edit</Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(false); void load(); }} data-testid="cancel-client-edit">Cancel</Button>
                    <Button size="sm" variant="gradient" onClick={() => void saveClientEdit()} disabled={savingEdit} data-testid="save-client-edit"><Save size={14} className="mr-1" /> {savingEdit ? 'Saving\u2026' : 'Save'}</Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1"><Label>Legal name *</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-client-name" /></div>
                  <div className="space-y-1"><Label>Trading name</Label><Input value={edit.trading_name} onChange={(e) => setEdit({ ...edit, trading_name: e.target.value })} data-testid="edit-client-trading" /></div>
                  <div className="space-y-1"><Label>Industry</Label><Input value={edit.industry} onChange={(e) => setEdit({ ...edit, industry: e.target.value })} placeholder="Retail, Hospitality, Cash-in-Transit\u2026" data-testid="edit-client-industry" /></div>
                  <div className="space-y-1"><Label>Registration number</Label><Input value={edit.registration_number} onChange={(e) => setEdit({ ...edit, registration_number: e.target.value })} className="font-tabular" data-testid="edit-client-regnum" /></div>
                  <div className="space-y-1"><Label>VAT number</Label><Input value={edit.vat_number} onChange={(e) => setEdit({ ...edit, vat_number: e.target.value })} className="font-tabular" data-testid="edit-client-vat" /></div>
                  <div className="space-y-1"><Label>Primary contact name</Label><Input value={edit.primary_contact_name} onChange={(e) => setEdit({ ...edit, primary_contact_name: e.target.value })} data-testid="edit-client-contact-name" /></div>
                  <div className="space-y-1"><Label>Contact phone</Label><Input value={edit.primary_contact_phone} onChange={(e) => setEdit({ ...edit, primary_contact_phone: e.target.value })} placeholder="0821234567 or +27821234567" className="font-tabular" data-testid="edit-client-contact-phone" /></div>
                  <div className="space-y-1"><Label>Contact email</Label><Input type="email" value={edit.primary_contact_email} onChange={(e) => setEdit({ ...edit, primary_contact_email: e.target.value })} data-testid="edit-client-contact-email" /></div>
                  <div className="space-y-1 md:col-span-2">
                    <Label>Broker</Label>
                    <select
                      className="h-9 w-full rounded-md border border-border/60 bg-white px-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
                      value={edit.broker_id}
                      onChange={(e) => setEdit({ ...edit, broker_id: e.target.value })}
                      data-testid="edit-client-broker"
                    >
                      <option value="">\u2014 No broker (house/direct book) \u2014</option>
                      {brokerOptions.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.brokerage_name} (FSP {b.fsp_number})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-raptor-navy/50">Assign later once the introducing broker is known. Leaving empty puts this client in the direct/house book.</p>
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label>Physical address</Label>
                    <AddressAutocomplete
                      value={edit.physical_address}
                      onChange={(v: string) => setEdit({ ...edit, physical_address: v })}
                      onResolve={(p) => setEdit({ ...edit, physical_address: p.formatted_address })}
                      testid="edit-client-address"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2"><Label>Notes</Label>
                    <textarea rows={3} className="w-full rounded-md border border-border/60 bg-white px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-raptor-blue/40" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="edit-client-notes" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm font-body">
                    <FieldRow label="Legal name" value={client.name} />
                    <FieldRow label="Trading name" value={client.trading_name} />
                    <FieldRow label="Industry" value={client.industry} />
                    <FieldRow label="Registration number" value={client.registration_number} mono />
                    <FieldRow label="VAT number" value={client.vat_number} mono />
                    <FieldRow label="Broker" value={client.broker_name ? `${client.broker_name} (FSP ${client.broker_fsp || '\u2014'})` : '\u2014'} />
                    <FieldRow label="Primary contact" value={client.primary_contact_name} />
                    <FieldRow label="Contact phone" value={client.primary_contact_phone} mono />
                    <FieldRow label="Contact email" value={client.primary_contact_email} />
                    <FieldRow label="Physical address" value={client.physical_address} />
                    <FieldRow label="Created" value={fmtDate(client.created_at)} />
                    <FieldRow label="Sites on file" value={String(sites.length)} />
                  </div>
                  {client.notes && (
                    <div className="mt-4 rounded-md border border-border/60 bg-raptor-neutral/30 p-3 text-sm font-body">
                      <div className="text-xs uppercase text-raptor-navy/50 mb-1">Notes</div>
                      <div className="text-raptor-navy whitespace-pre-wrap">{client.notes}</div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><FileText size={16} className="text-raptor-blue" /> Confirmations of Cover · {covers.length}</CardTitle>
                <CardDescription>Latest cover documents linked to this client. Click any row to open.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => nav(`/insurer/covers?client_id=${client.id}`)} data-testid="view-all-covers">
                View all <ExternalLink size={12} className="ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {covers.length === 0 ? (
                <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">No confirmations of cover yet.</div>
              ) : (
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                    <tr>
                      <th className="py-2 px-3 text-left">Cover</th>
                      <th className="py-2 px-3 text-left">Status</th>
                      <th className="py-2 px-3 text-left">Period</th>
                      <th className="py-2 px-3 text-right">Sum insured</th>
                      <th className="py-2 px-3 text-right">Premium</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {covers.slice(0, 5).map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                        onClick={() => nav(`/insurer/covers/${c.id}`)}
                        data-testid={`client-cover-row-${c.id}`}
                      >
                        <td className="py-2 px-3 font-tabular">{c.cover_number}</td>
                        <td className="py-2 px-3">
                          {c.status === 'issued' ? <Badge variant="success">Issued</Badge> : <Badge variant="muted">Draft</Badge>}
                        </td>
                        <td className="py-2 px-3 text-xs">{fmtDate(c.policy_period_start)} → {fmtDate(c.policy_period_end)}</td>
                        <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_sum_insured_zar)}</td>
                        <td className="py-2 px-3 text-right font-tabular">{fmtZAR(c.totals?.total_premium_zar)}</td>
                        <td className="py-2 px-3 text-right"><ChevronRight size={14} className="text-raptor-navy/40 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 2: All Sites (with search) ---------- */}
        <TabsContent value="sites" className="mt-4">
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><MapPin size={16} className="text-raptor-blue" /> All sites · {filteredSites.length}{siteFilter && ` of ${sites.length}`}</CardTitle>
                <CardDescription>Every physical location this client operates. Click a row to open the site.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
                  <Input
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    placeholder="Search name, ref, city…"
                    className="h-8 text-sm w-64 pl-7"
                    data-testid="site-search"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => nav(`/insurer/sites?client_id=${client.id}`)} data-testid="add-manage-sites">
                  <Plus size={14} /> Add / manage sites
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredSites.length === 0 ? (
                <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">
                  {sites.length === 0 ? 'No sites under this client yet.' : 'No sites match that search.'}
                </div>
              ) : (
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
                    <tr>
                      <th className="py-2 px-3 text-left">Site</th>
                      <th className="py-2 px-3 text-left">Reference</th>
                      <th className="py-2 px-3 text-left">City</th>
                      <th className="py-2 px-3 text-left">Province</th>
                      <th className="py-2 px-3 text-left">Contact</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSites.map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                        onClick={() => nav(`/insurer/sites/${s.id}`)}
                        data-testid={`site-row-${s.id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="font-semibold text-raptor-navy">{s.name}</div>
                          {s.physical_address && <div className="text-xs text-raptor-navy/50">{s.physical_address}</div>}
                        </td>
                        <td className="py-2 px-3 text-xs font-tabular text-raptor-navy/70">{s.site_reference || '—'}</td>
                        <td className="py-2 px-3 text-xs text-raptor-navy/70">{s.city || '—'}</td>
                        <td className="py-2 px-3 text-xs text-raptor-navy/70">{s.province || '—'}</td>
                        <td className="py-2 px-3 text-xs">
                          {s.site_contact_name || '—'}
                          {s.site_contact_phone && <div className="text-raptor-navy/50 font-tabular">{s.site_contact_phone}</div>}
                        </td>
                        <td className="py-2 px-3 text-right"><ChevronRight size={14} className="text-raptor-navy/40 inline" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 3: Users ---------- */}
        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Users2 size={16} className="text-raptor-blue" /> Users · {users.length}</CardTitle>
              <CardDescription>People at {client.name} who will access the client portal. Same whitelist pattern as broker approved staff.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={addUser} className="grid md:grid-cols-5 gap-3 mb-4" data-testid="client-user-form">
                <div className="space-y-1">
                  <Label>Full name *</Label>
                  <Input data-testid="client-user-name" required placeholder="e.g. John Smith" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Mobile *</Label>
                  <Input data-testid="client-user-phone" required placeholder="0821234567 or +27821234567" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input data-testid="client-user-email" type="email" placeholder="john@company.co.za" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <Input data-testid="client-user-role" placeholder="Client Admin" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} />
                </div>
                <div className="flex items-end">
                  <Button data-testid="client-user-add" type="submit" variant="gradient" className="w-full"><Plus size={14} className="mr-1" /> Add</Button>
                </div>
              </form>

              <table className="w-full text-sm font-body">
                <thead className="bg-raptor-neutral/40 text-xs text-raptor-navy/70 uppercase tracking-wide">
                  <tr>
                    <th className="py-2 px-3 text-left">ID</th>
                    <th className="py-2 px-3 text-left">Name</th>
                    <th className="py-2 px-3 text-left">Phone</th>
                    <th className="py-2 px-3 text-left">Role</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left">Registered</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-raptor-navy/50" data-testid="client-users-empty">No users approved yet.</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-border/60" data-testid={`client-user-row-${u.id}`}>
                      <td className="py-2 px-3 font-tabular">{u.id}</td>
                      <td className="py-2 px-3 font-semibold">
                        {u.full_name}
                        {primaryContactPhone && u.phone && primaryContactPhone.replace(/[^0-9+]/g, '') === u.phone.replace(/[^0-9+]/g, '') && (
                          <Badge variant="blue" className="ml-1 text-[9px]">Primary contact</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 font-tabular">{u.phone}</td>
                      <td className="py-2 px-3">{u.role}</td>
                      <td className="py-2 px-3">{u.access_status === 'Active' ? <Badge variant="success">Active</Badge> : <Badge variant="destructive">Revoked</Badge>}</td>
                      <td className="py-2 px-3">{u.registered ? <Badge variant="blue">Registered</Badge> : <Badge variant="muted">Pending</Badge>}</td>
                      <td className="py-2 px-3 flex gap-1">
                        {u.access_status === 'Active' ? (
                          <>
                            <Button data-testid={`client-user-resend-${u.id}`} size="sm" variant="ghost" disabled={resendingUser === u.id || (!u.email && !u.phone)} onClick={() => resendUserWelcome(u.id)} title="Re-send the welcome / portal-access message"><Send size={12} className="mr-1" /> {resendingUser === u.id ? 'Sending…' : 'Resend'}</Button>
                            <Button data-testid={`client-user-revoke-${u.id}`} size="sm" variant="outline" onClick={() => revokeUser(u.id)}>Revoke</Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => restoreUser(u.id)}><RefreshCw size={12} className="mr-1" /> Restore</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)}><Trash2 size={14} className="text-red-600" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-[11px] text-raptor-navy/50 font-body flex items-center gap-1">
                <ShieldCheck size={12} /> Users can log in once the client-facing portal is enabled. Their phone is used for SMS OTP.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Tab 4: Site Contacts ---------- */}
        <TabsContent value="site-contacts" className="mt-4 space-y-4">
          {clientId ? <SiteContactsTab clientId={clientId} sites={sites} /> : null}
        </TabsContent>

        {/* ---------- Tab 5: Site Intake (read-only) ---------- */}
        <TabsContent value="intake" className="mt-4 space-y-4">
          {clientId ? <SiteIntakeReadonly clientId={clientId} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const FieldRow: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="min-w-0">
    <div className="text-xs uppercase tracking-wide text-raptor-navy/50 mb-0.5">{label}</div>
    <div className={`text-sm text-raptor-navy break-words ${mono ? 'font-tabular' : ''} ${!value ? 'text-raptor-navy/40 italic' : ''}`}>
      {value || '—'}
    </div>
  </div>
);

export default ClientDetailPage;
